import { motion, AnimatePresence, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { useNavigate } from 'react-router-dom'
import { X, ShoppingBag, Trophy, Flame, CalendarDays, Camera, MessageSquareHeart, Target } from 'lucide-react'
import { TuCoinIcon } from '../TuCoinIcon'
import { TfaLogo } from '../TfaLogo'
import { CHALLENGE_COMPLETION_TUCOIN } from '@tfa/shared'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  balance: number
}

// TuCoin начисляется не за каждое действие, а за 5 каналов экономики.
// Действия (тренировки/питание/замеры) дают опыт (XP) и качают уровни.
const EARN_WAYS = [
  { Icon: Flame,              label: 'Ударный режим — недельные чек-поинты',     value: '+2' },
  { Icon: CalendarDays,       label: 'Каждые 30 дней с нами',                     value: '+3' },
  { Icon: Trophy,             label: 'Новый уровень персонажа',                   value: '+1…+16' },
  { Icon: Camera,             label: 'Контрольное фото месяца',                   value: '+1/+2' },
  { Icon: Camera,             label: 'Финальное фото До/После',                   value: '+10' },
  { Icon: MessageSquareHeart, label: 'Анкета обратной связи (экватор)',           value: '+2' },
  { Icon: MessageSquareHeart, label: 'Итоговая анкета NPS',                       value: '+10' },
  // #474: за челлендж монета одна и только за полностью пройденный срок.
  // Строка «Участие +2» описывала старый флоу, который на проде не начислил
  // ни одной монеты, — и вместе с «+7 Тукоин» на карточке рождала ожидание,
  // которого система не выполняла.
  { Icon: Target,             label: 'Челлендж пройден целиком',                  value: `+${CHALLENGE_COMPLETION_TUCOIN}` },
] as const

const SPEND_WAYS = [
  'Купить аксессуары и заморозки для персонажа в Магазине',
  'Восстановить потерянные жизни',
  'Открыть бонусный контент / премиум-лекции',
  'Использовать как голос при голосовании за призы недели',
] as const

export function TuCoinInfoSheet({ open, onClose, balance }: Props) {
  const navigate = useNavigate()

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="tucoin-info-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4 }}>
              <SheetGrip onClose={onClose} />
            </div>

            {/* Balance hero */}
            <div style={{ position: 'relative', padding: '12px 20px 20px' }}>
              <button
                data-testid="tucoin-close"
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 12, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, paddingTop: 8 }}>
                {/* Монетка TuCoin — рельефная: два металлических кольца + блик + большое лого */}
                <div style={{
                  position: 'relative',
                  width: 96, height: 96, borderRadius: '50%',
                  background: 'radial-gradient(circle at 30% 25%, #FFE89A 0%, #FFC940 35%, #E08300 75%, #A85B00 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow:
                    '0 14px 34px rgba(255,149,0,0.45),' +
                    '0 0 0 2.5px rgba(255,236,170,0.95) inset,' +
                    '0 0 0 5.5px rgba(184,90,0,0.55) inset,' +
                    '0 0 0 8px rgba(255,236,170,0.55) inset',
                }}>
                  {/* Блик в углу */}
                  <span aria-hidden style={{
                    position: 'absolute', top: 8, left: 16,
                    width: 32, height: 18, borderRadius: '50%',
                    background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.65) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  }} />
                  {/* Логотип ТФА — крупно, занимает почти всю монету */}
                  <span style={{
                    width: 70, height: 70, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%)',
                    position: 'relative', zIndex: 1,
                  }}>
                    <TfaLogo size={64} />
                  </span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA5B4', letterSpacing: 1, textTransform: 'uppercase' }}>
                  Баланс TuCoin
                </div>
                <div style={{ fontSize: 38, fontWeight: 800, color: '#1C2D41', lineHeight: 1 }}>
                  {balance.toLocaleString('ru-RU')}
                </div>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '0 16px 8px' }}>
              {/* What is it */}
              <div style={{
                background: 'rgba(108,92,231,0.06)',
                border: '1px solid rgba(108,92,231,0.16)',
                borderRadius: 14, padding: 12,
                marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', marginBottom: 4 }}>
                  Что это такое
                </div>
                <p style={{ fontSize: 12, color: '#3D4A5C', margin: 0, lineHeight: 1.5 }}>
                  TuCoin — внутренняя валюта программы. Её нельзя купить за деньги — только заработать: за ударный режим, развитие персонажа, контрольные фото и анкеты, полностью пройденные челленджи. Тратится в Магазине на аксессуары персонажа, восстановление жизней, заморозки и бонусный контент.
                </p>
                <p style={{ fontSize: 11, color: '#9AA5B4', margin: '6px 0 0', lineHeight: 1.5 }}>
                  Идеальный сценарий: <b style={{ color: '#1C2D41' }}>81 TuCoin</b> за поток (3 месяца), <b style={{ color: '#1C2D41' }}>112</b> за Клуб (6 месяцев).
                </p>
              </div>

              {/* How to earn */}
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', margin: '4px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>💰</span> Как заработать
              </div>
              <div style={{
                background: 'white',
                borderRadius: 14, padding: '4px 0',
                border: '1px solid rgba(26,58,92,0.06)',
                marginBottom: 14,
              }}>
                {EARN_WAYS.map((w, i) => {
                  const Icon = w.Icon
                  return (
                    <div
                      key={w.label}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px',
                        borderTop: i === 0 ? 'none' : '1px solid rgba(26,58,92,0.05)',
                      }}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: 9,
                        background: 'rgba(124,58,237,0.10)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon size={14} color="#7C3AED" />
                      </div>
                      <span style={{ flex: 1, fontSize: 12, color: '#1C2D41' }}>{w.label}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 800, color: '#7C3AED',
                        background: 'rgba(124,58,237,0.10)',
                        borderRadius: 8, padding: '3px 8px',
                        display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                      }}>
                        {w.value} <TuCoinIcon size={12} />
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* How to spend */}
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', margin: '4px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>🛍️</span> Где потратить
              </div>
              <div style={{
                background: 'white',
                borderRadius: 14, padding: '4px 0',
                border: '1px solid rgba(26,58,92,0.06)',
                marginBottom: 14,
              }}>
                {SPEND_WAYS.map((text, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid rgba(26,58,92,0.05)',
                    }}
                  >
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: 'rgba(255,149,0,0.12)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, color: '#FF9500',
                      flexShrink: 0,
                    }}>
                      {i + 1}
                    </span>
                    <span style={{ fontSize: 12, color: '#1C2D41', lineHeight: 1.5 }}>{text}</span>
                  </div>
                ))}
              </div>

              <button
                data-testid="tucoin-open-shop"
                onClick={() => { onClose(); navigate('/shop') }}
                style={{
                  width: '100%', height: 50, borderRadius: 14, border: 'none',
                  background: 'linear-gradient(135deg, #FF9500 0%, #FF6B00 100%)',
                  color: 'white', fontSize: 15, fontWeight: 800,
                  cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  boxShadow: '0 8px 22px rgba(255,149,0,0.34)',
                }}
              >
                <ShoppingBag size={17} /> Открыть магазин
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
